import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  addBankStatementApi,
  getBankStatementApi,
  updateBankStatementApi,
} from "../../../Api/BankStatement";
import { IconX } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { getAllBankApi } from "../../../Api/Bank";

interface Option {
  id: number;
  value: string;
  label: string;
}

const CrDrtypes: Option[] = [
  { id: 1, value: "CR", label: "CR" },
  { id: 2, value: "DR", label: "DR" },
];

interface BankStatementFormProps {
  url: string;
  add: boolean;
  setAdd: React.Dispatch<React.SetStateAction<boolean>>;
  Id?: string | null;
  onSuccess?: () => void;
}

export const BankStatementForm: React.FC<BankStatementFormProps> = ({
  url,
  add,
  setAdd,
  Id,
  onSuccess,
}) => {
  const [TransactionId, setTransactionId] = useState<string | null>(null);
  const [ValueDate, setValueDate] = useState<string>("");
  const [TxnPostedDate, setTxnPostedDate] = useState<Date | null>(null);
  const [ChequeNo, setChequeNo] = useState<string>("");
  const [Description, setDescription] = useState<string>("");
  const [TransactionAmount, setTransactionAmount] = useState<string>("");
  const [Balance, setBalance] = useState<string>("");
  const [CR_DR, setCrDr] = useState<Option | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [bankId, setBankId] = useState<string | null>(null);
  const [banks, setBanks] = useState<{ id: string; name: string }[]>([]);
  const location = useLocation();
  const { id: locationId } = (location.state as { id?: string }) || {};
  const bankSTId = Id || locationId;

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await getAllBankApi(url);
        setBanks(response);
      } catch (error) {
        console.error("Failed to fetch banks", error);
      }
    };

    fetchBanks();
  }, []);

  useEffect(() => {
    async function fetchBankST() {
      if (bankSTId) {
        try {
          const bankSTData = await getBankStatementApi(url, bankSTId);
          const bankSTDataToEdit = Array.isArray(bankSTData)
            ? bankSTData.find((bankst: any) => bankst.id === bankSTId)
            : bankSTData;

          if (bankSTDataToEdit) {
            setBankId(bankSTDataToEdit.bankId);
            setTransactionId(bankSTDataToEdit.transactionId);
            setValueDate(bankSTDataToEdit.valueDate);
            setTxnPostedDate(bankSTDataToEdit.txnPostedDate);
            setChequeNo(bankSTDataToEdit.chequeNo);
            setDescription(bankSTDataToEdit.description);

            const crDrValue = bankSTDataToEdit.crDr || bankSTDataToEdit.cR_DR;
            if (crDrValue && typeof crDrValue === "string") {
              const matchingOption = CrDrtypes.find(
                (opt) => opt.value.toUpperCase() === crDrValue.toUpperCase()
              );
              setCrDr(matchingOption || null);
            } else {
              setCrDr(null);
            }

            setTransactionAmount(bankSTDataToEdit.transactionAmount);
            setBalance(bankSTDataToEdit.balance);
            setIsEdit(true);
          }
        } catch (error) {
          toast.error("Error fetching bank statement data.", error);
        }
      }
    }
    fetchBankST();
  }, [bankSTId, url]);

  const handleSubmitClick = async (event: React.FormEvent) => {
    event.preventDefault();

    if (
      !ValueDate ||
      !ChequeNo ||
      !Description ||
      !CR_DR ||
      !TransactionAmount ||
      !Balance
    ) {
      toast.error("Please fill all the fields");
      return;
    }

    try {
      if (isEdit && bankSTId) {
        await updateBankStatementApi(
          bankSTId,
          bankId,
          ValueDate,
          ChequeNo,
          Description,
          CR_DR.value,
          TransactionAmount,
          Balance
        );
        if (onSuccess) onSuccess();
        toast.success("Updated Successfully");
      } else {
        await addBankStatementApi(
          bankId,
          TransactionId,
          ValueDate,
          ChequeNo,
          Description,
          CR_DR.value,
          TransactionAmount,
          Balance
        );
        if (onSuccess) onSuccess();
        toast.success("Added Successfully");
      }
    } catch (error) {
      toast.error("Error during submission:", error);
    }

    handleResetForm();
  };

  const handleResetForm = () => {
    setTransactionId("");
    setValueDate("");
    setTxnPostedDate(null);
    setChequeNo("");
    setDescription("");
    setCrDr(null);
    setTransactionAmount("");
    setBalance("");
    setAdd(false);
  };

  return (
    <div className="px-4">
      <div className="flex justify-between items-center border-b pb-2 mb-4">
        <h3 className="text-xl font-semibold text-blue-700">
          {isEdit ? "Update Bank Statement" : "Add New Bank Statement"}
        </h3>
    
      </div>

      <form onSubmit={handleSubmitClick} className="mt-8 space-y-4">
        <div className="flex flex-col space-y-2">
          <label className="font-medium text-black">Bank</label>
          <select
            className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            required
            value={bankId || ""}
            onChange={(e) => setBankId(e.target.value || null)}
          >
            <option value="">Select</option>
            {banks.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.name}
              </option>
            ))}
          </select>
        </div>



        <div className="flex flex-col space-y-2">
          <label className="font-medium text-black">Cheque No</label>
          <input
            className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={ChequeNo}
            required
            onChange={(e) => setChequeNo(e.target.value)}
          />
        </div>

        <div className="flex flex-col space-y-2">
          <label className="font-medium text-black">Value Date</label>
          <input
            className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            type="date"
            value={ValueDate}
            required
            onChange={(e) => setValueDate(e.target.value)}
          />
        </div>

        <div className="flex flex-col space-y-2">
          <label className="font-medium text-black">Transaction Amount</label>
          <input
            className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={TransactionAmount}
            required
            onChange={(e) => setTransactionAmount(e.target.value)}
          />
        </div>

        <div className="flex flex-col space-y-2">
          <label className="font-medium text-black">Description</label>
          <textarea
            className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={Description}
            required
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex flex-col space-y-2">
          <label className="font-medium text-black">Transaction Type</label>
          <select
            className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={CR_DR?.value || ""}
            required
            onChange={(e) => {
              const type = CrDrtypes.find(
                (option) => option.value === e.target.value
              );
              setCrDr(type || null);
            }}
          >
            <option value="">Select</option>
            {CrDrtypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col space-y-2">
          <label className="font-medium text-black">Balance</label>
          <input
            className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={Balance}
            required
            onChange={(e) => setBalance(e.target.value)}
          />
        </div>

        <div className="flex justify-start mt-4">
          <button
            type="submit"
            className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 mr-4"
          >
            Submit
          </button>
          <button
            type="button"
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md"
            onClick={handleResetForm}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
